import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BasicService } from '../../providers/basic';
import { environment as config } from '../../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AuthHttp } from 'angular2-jwt';
import { AuthService } from '../auth/auth.service';
import { ISession } from '../../models/session';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';

export const FIELD_TYPES = [
  'text', 'email', 'radio', 'select', 'checkbox', 'label', 'multiple', 'textarea', 'accompagnants'
];

export const MULTIVALUE_TYPES = [
  'radio', 'select', 'multiple'
];

export const FIELD_TYPE_ICONS: { [type: string]: string } = {
  text: 'fa-font',
  email: 'fa-envelope',
  radio: 'fa-dot-circle-o',
  select: 'fa-caret-square-o-down',
  checkbox: 'fa-check-square-o',
  label: 'fa-tag',
  multiple: 'fa-list-ul',
  textarea: 'fa-align-left',
  accompagnants: 'fa-users',
};

export interface IFieldValue {
  option_value_id: number;
  label: string;
  quota?: number;
  quota_remaining?: number;
  allow_waiting?: boolean;
  product_id?: string;
  is_segment_value?: boolean;
  questions_to_display?: number[];
  companions_data?: ICompanionsData;
}

export interface ICompanionsDataLimit {
  option_value_id: number;
  limit: number;
}
export interface ICompanionsData {
  companions_options: 'global' | 'conditional' | 'individual';
  global_value?: number;
  linked_option_id?: number;
  limits?: ICompanionsDataLimit[];
  number_option_id?: number;
}

export interface IRegistrationField {
  option_id: number;
  name?: string;
  label?: string;
  field_type: string;
  column_name: string;
  is_default: boolean;
  is_in_use?: boolean;
  required?: boolean;
  required_dependent?: boolean;
  hide_field?: boolean;
  sort_order?: number;
  display_remaining_quota?: 'no' | 'values' | 'indicator';
  is_segment?: boolean;
  for_companion?: boolean;
  display_for_contact_tag?: boolean;
  display_on_event_manager?: boolean;
  display_in_live_scan?: boolean;
  companions_data?: ICompanionsData;
  values?: IFieldValue[];
  group?: string;
}

export interface IRegistrationFormOptions {
  inUseOnly: boolean;
  displayKind: boolean;
  allowQuotas: boolean;
  allowCompanions: boolean;
  allowConditions: boolean;
  allowWaitingList: boolean;
}

export interface IRegistrationForm {
  session_id: number;
  language: string;
  fields: IRegistrationField[];
}

@Injectable()
export class RegistrationFormService extends BasicService {
  private API = config.usersv3.url;

  constructor(
    protected authService: AuthService,
    protected translate: TranslateService,
    private http: AuthHttp,
  ) {
    super(authService, translate);
  }

  private handleRegistrationFormError(error: any) {
    if (error.status === 409) {
      toastr.error(this.translate.instant('action.alreadyFilled'));
      return Observable.throw('Conflict');
    }
    return this.handleError(error);
  }

  private formUrl(session: ISession): string {
    return `${this.API}/sessions/${session.id}/registration_form`;
  }

  public getRegistrationForm(session: ISession, notify: boolean = false): Observable<IRegistrationForm> {
    return this.http
      .get(`${this.formUrl(session)}?lang=${session.language}`)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public updateDefaultField(session: ISession, fieldId: number, dto: object, notify: boolean = true): Observable<any> {
    return this.http
      .put(`${this.formUrl(session)}/default_field/${fieldId}`, dto)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public createCustomField(session: ISession, dto: object, notify: boolean = true): Observable<IRegistrationField> {
    return this.http
      .post(`${this.formUrl(session)}/custom_field`, dto)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public updateCustomField(session: ISession, optionId: number, dto: object, notify: boolean = true): Observable<IRegistrationField> {
    return this.http
      .put(`${this.formUrl(session)}/custom_field/${optionId}`, dto)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public deleteCustomField(session: ISession, optionId: number, notify: boolean = true): Observable<any> {
    return this.http
      .delete(`${this.formUrl(session)}/custom_field/${optionId}`)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public createCustomFieldValue(session: ISession, optionId: number, dto: object, notify: boolean = true): Observable<IFieldValue> {
    return this.http
      .post(`${this.formUrl(session)}/custom_field/${optionId}/value`, dto)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public updateCustomFieldValue(session: ISession, optionId: number, valueId: number, dto: object, notify: boolean = true): Observable<IFieldValue> {
    return this.http
      .put(`${this.formUrl(session)}/custom_field/${optionId}/value/${valueId}`, dto)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public deleteCustomFieldValue(session: ISession, optionId: number, valueId: number, notify: boolean = true): Observable<any> {
    return this.http
      .delete(`${this.formUrl(session)}/custom_field/${optionId}/value/${valueId}`)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public updateDefaultFieldValue(session: ISession, optionId: number, valueId: number, dto: object, notify: boolean = true): Observable<IFieldValue> {
    return this.http
      .put(`${this.formUrl(session)}/default_field/${optionId}/value/${valueId}`, dto)
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public setValueConditions(session: ISession, valueId: number, questionsToDisplay: number[], notify: boolean = true): Observable<any> {
    return this.http
      .put(`${this.formUrl(session)}/value/${valueId}/conditions`, { questions_to_display: questionsToDisplay })
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public reorderFields(session: ISession, order: Array<{ option_id: number; is_default: boolean }>, notify: boolean = true): Observable<any> {
    return this.http
      .put(`${this.formUrl(session)}/reorder`, { order })
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public reorderCustomFieldValues(session: ISession, optionId: number, order: number[], notify: boolean = true): Observable<any> {
    return this.http
      .put(`${this.formUrl(session)}/custom_field/${optionId}/values/reorder`, { order })
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }

  public setCompanions(session: ISession, optionId: number, companionsData: ICompanionsData, notify: boolean = true): Observable<any> {
    return this.http
      .put(`${this.formUrl(session)}/custom_field/${optionId}/companions`, { companions_data: companionsData })
      .map(res => this.extractData(res, notify))
      .catch(err => this.handleRegistrationFormError(err));
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/registration-form.service.ts